import { ICountResponse, IPayoutSummary, IReservationCounts, RESERVATION_OVERDUE_HOURS } from '@curbnturf/entities';

/** A request counts as overdue once it has waited this long since its last status change. */
export const OVERDUE_HOURS = RESERVATION_OVERDUE_HOURS;

/** What needs an admin's attention. `null` means that count couldn't be loaded. */
export interface AttentionCounts {
  /** Booking requests, change requests and payments in processing. */
  requests: number | null;
  /** Of `requests`, how many have waited over OVERDUE_HOURS. */
  overdueRequests: number | null;
  /** POIs suggested but not yet approved. */
  pois: number | null;
  caretakers: number | null;
  payouts: number | null;
  /** Total requested by the pending payouts, in dollars. */
  payoutAmount: number | null;
  /** Listings not yet published (not deleted). */
  unpublished: number | null;
  /** Affiliates with earnings not yet assigned. */
  affiliates: number | null;
  /** When the last refresh finished. */
  updatedAt?: Date;
}

export const NO_COUNTS: AttentionCounts = {
  requests: null,
  overdueRequests: null,
  pois: null,
  caretakers: null,
  payouts: null,
  payoutAmount: null,
  unpublished: null,
  affiliates: null,
};

type Settled<T> = PromiseSettledResult<T>;
const value = <T>(r: Settled<T>): T | undefined => (r.status === 'fulfilled' ? r.value : undefined);
const num = (n: unknown): number => Number(n) || 0;

/** Each source settles on its own: one failing leaves its counts null and the rest intact. */
export function countAttention(
  sources: {
    reservations: Settled<IReservationCounts>;
    pois: Settled<ICountResponse>;
    caretakers: Settled<ICountResponse>;
    payouts: Settled<IPayoutSummary>;
    unpublished: Settled<{ total: number }>;
    affiliates: Settled<ICountResponse>;
  },
  now: Date = new Date(),
): AttentionCounts {
  const reservations = value(sources.reservations);
  const pois = value(sources.pois);
  const caretakers = value(sources.caretakers);
  const payouts = value(sources.payouts);
  const unpublished = value(sources.unpublished);
  const affiliates = value(sources.affiliates);
  return {
    requests: reservations ? num(reservations.waiting) : null,
    overdueRequests: reservations ? num(reservations.overdue) : null,
    pois: pois ? num(pois.count) : null,
    caretakers: caretakers ? num(caretakers.count) : null,
    payouts: payouts ? num(payouts.count) : null,
    payoutAmount: payouts ? num(payouts.total) : null,
    unpublished: unpublished ? num(unpublished.total) : null,
    affiliates: affiliates ? num(affiliates.count) : null,
    updatedAt: now,
  };
}
